import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { queriesApi, waitlistApi, settingsApi, geocodeApi, ctosApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  MapPin,
  Check,
  X,
  UserPlus,
  LogOut,
  History,
  List,
  Wifi,
  Navigation,
  ChevronRight,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom CTO Icon
const ctoIcon = L.divIcon({
  className: "custom-cto-icon",
  html: `<div style="
    width: 28px;
    height: 28px;
    background: #FF4500;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 3px 10px rgba(255,69,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
    </svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Query Location Icon - Viable
const viableIcon = L.divIcon({
  className: "custom-viable-icon",
  html: `<div style="
    width: 32px;
    height: 32px;
    background: #00C853;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 3px 10px rgba(0,200,83,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Query Location Icon - Not Viable
const notViableIcon = L.divIcon({
  className: "custom-not-viable-icon",
  html: `<div style="
    width: 32px;
    height: 32px;
    background: #D50000;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 3px 10px rgba(213,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Map Center Controller
const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 15);
    }
  }, [center, zoom, map]);
  return null;
};

export default function SalesDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  
  const [searchAddress, setSearchAddress] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [addressValidated, setAddressValidated] = useState(false); // Track if user selected from autocomplete
  const [queryResult, setQueryResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({ client_name: "", phone: "" });
  const [maxDistance, setMaxDistance] = useState(300);
  const [mapCenter, setMapCenter] = useState([-22.77, -47.41]); // Santa Bárbara d'Oeste default
  const [mapZoom, setMapZoom] = useState(13);
  const [nearestCto, setNearestCto] = useState(null);
  const [lastKmzUpdate, setLastKmzUpdate] = useState(null);
  const searchTimeoutRef = useRef(null);
  const lastSelectedAddressRef = useRef(null); // Track last selected address to prevent race condition

  // Load settings and last update on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, lastUpdateRes] = await Promise.all([
          settingsApi.get(),
          ctosApi.getLastUpdate()
        ]);
        setMaxDistance(settingsRes.data.max_distance_meters);
        if (lastUpdateRes.data?.uploaded_at) {
          setLastKmzUpdate(lastUpdateRes.data.uploaded_at);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    loadData();
  }, []);

  // Nominatim geocoding search via backend
  const searchAddressGeocode = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    // Skip search if this is the address user just selected (prevents race condition)
    if (query === lastSelectedAddressRef.current) {
      return;
    }
    
    // Reset validation when user types (didn't select from list)
    setAddressValidated(false);
    setSelectedLocation(null);
    
    setSearching(true);
    try {
      const response = await geocodeApi.search(query);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error("Geocoding error:", error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search - 300ms para autocomplete mais rápido
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchAddressGeocode(searchAddress);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchAddress, searchAddressGeocode]);

  // Select address from search results
  const handleSelectAddress = (result) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    
    // Store the selected address to prevent race condition with debounced search
    lastSelectedAddressRef.current = result.display_name;
    
    setSelectedLocation({
      address: result.display_name,
      latitude: lat,
      longitude: lon,
    });
    setAddressValidated(true); // User selected from autocomplete = valid
    setMapCenter([lat, lon]);
    setMapZoom(16);
    setSearchResults([]);
    setSearchAddress(result.display_name);
    setQueryResult(null);
    setNearestCto(null);
  };

  // Check viability
  const handleCheckViability = async () => {
    // Validação 1: Precisa ter selecionado do autocomplete
    if (!addressValidated || !selectedLocation) {
      toast.error("⚠️ Selecione um endereço da lista de sugestões!", {
        description: "Digite o endereço e clique em uma das opções que aparecem.",
        duration: 5000,
      });
      return;
    }

    // Validação 2: Precisa ter coordenadas válidas
    if (!selectedLocation.latitude || !selectedLocation.longitude) {
      toast.error("❌ Endereço sem coordenadas válidas", {
        description: "Por favor, selecione outro endereço da lista.",
        duration: 5000,
      });
      return;
    }

    setChecking(true);
    try {
      const response = await queriesApi.check({
        address: selectedLocation.address,
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
      });
      
      setQueryResult(response.data);
      
      if (response.data.is_viable) {
        toast.success("✅ Endereço viável para instalação!", {
          description: `CTO: ${response.data.nearest_cto_name} | Distância: ${response.data.distance_meters.toFixed(0)}m`,
        });
      } else {
        toast.error(`❌ Endereço não viável`, {
          description: `Distância até a rede: ${response.data.distance_meters.toFixed(0)}m (máximo: ${maxDistance}m)`,
          duration: 6000,
        });
      }

      // Store nearest CTO info for display (only load this one CTO, not all)
      if (response.data.nearest_cto_id && response.data.nearest_cto_name) {
        // Use route geometry endpoint coordinates if available
        if (response.data.route_geometry && response.data.route_geometry.length > 0) {
          const lastPoint = response.data.route_geometry[response.data.route_geometry.length - 1];
          setNearestCto({
            id: response.data.nearest_cto_id,
            name: response.data.nearest_cto_name,
            latitude: lastPoint[0],
            longitude: lastPoint[1]
          });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao verificar viabilidade");
    } finally {
      setChecking(false);
    }
  };

  // Add to waitlist
  const handleAddToWaitlist = async () => {
    if (!waitlistForm.client_name || !waitlistForm.phone) {
      toast.error("Preencha todos os campos");
      return;
    }

    try {
      await waitlistApi.add({
        client_name: waitlistForm.client_name,
        phone: waitlistForm.phone,
        address: selectedLocation.address,
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        nearest_cto_name: queryResult?.nearest_cto_name,
        distance_meters: queryResult?.distance_meters,
        reason: `Distância maior que ${maxDistance}m (${queryResult?.distance_meters?.toFixed(0)}m)`,
      });

      toast.success("Cliente adicionado à fila de espera!");
      setShowWaitlistModal(false);
      setWaitlistForm({ client_name: "", phone: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar à fila");
    }
  };

  // Get route line between query location and nearest CTO
  // Usa a geometria da rota real (seguindo ruas) se disponível
  const getConnectionLine = () => {
    if (!queryResult || !selectedLocation) return null;
    
    // Se temos a geometria da rota real, usar ela
    if (queryResult.route_geometry && queryResult.route_geometry.length > 0) {
      return queryResult.route_geometry;
    }
    
    // Fallback para linha reta se não tiver geometria mas tiver nearestCto
    if (!nearestCto) return null;

    return [
      [selectedLocation.latitude, selectedLocation.longitude],
      [nearestCto.latitude, nearestCto.longitude]
    ];
  };

  return (
    <div className="h-screen w-full relative bg-slate-950" data-testid="sales-dashboard">
      {/* Map Container */}
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        className="h-full w-full z-0"
        zoomControl={false}
        preferCanvas={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController center={mapCenter} zoom={mapZoom} />
        
        {/* Nearest CTO Marker - Only shown after viability check */}
        {nearestCto && queryResult && (
          <Marker 
            position={[nearestCto.latitude, nearestCto.longitude]} 
            icon={ctoIcon}
          >
            <Popup>
              <div className="p-2">
                <p className="font-bold text-slate-900">{nearestCto.name}</p>
                <p className="text-xs text-slate-600">CTO mais próxima</p>
                <p className="text-xs font-mono text-slate-500 mt-1">
                  {nearestCto.latitude.toFixed(6)}, {nearestCto.longitude.toFixed(6)}
                </p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Query Location Marker */}
        {selectedLocation && queryResult && (
          <Marker
            position={[selectedLocation.latitude, selectedLocation.longitude]}
            icon={queryResult.is_viable ? viableIcon : notViableIcon}
          >
            <Popup>
              <div className="p-2">
                <p className={`font-bold ${queryResult.is_viable ? 'text-green-600' : 'text-red-600'}`}>
                  {queryResult.is_viable ? 'VIÁVEL' : 'NÃO VIÁVEL'}
                </p>
                <p className="text-sm">{selectedLocation.address}</p>
                <p className="text-xs font-mono mt-1">
                  Distância: {queryResult.distance_meters?.toFixed(0)}m
                </p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Route Line - Segue as ruas onde passam os cabos */}
        {getConnectionLine() && (
          <Polyline
            positions={getConnectionLine()}
            color={queryResult?.is_viable ? "#00C853" : "#D50000"}
            weight={4}
            opacity={0.8}
          />
        )}
      </MapContainer>

      {/* Header Overlay */}
      <div className="absolute top-0 left-0 right-0 z-20 p-2 sm:p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img 
              src="/tecplus-logo.png" 
              alt="Tecplus Telecom" 
              className="h-10 sm:h-12 object-contain drop-shadow-lg"
            />
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <Link to="/history">
              <Button
                variant="ghost"
                size="icon"
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20"
                data-testid="history-link"
              >
                <History className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </Link>
            <Link to="/waitlist">
              <Button
                variant="ghost"
                size="icon"
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20"
                data-testid="waitlist-link"
              >
                <List className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Search Panel */}
      <div className="absolute top-16 sm:top-20 left-2 right-2 sm:left-4 sm:right-4 md:left-4 md:right-auto md:w-96 z-20">
        <div className="glass-card rounded-xl p-3 sm:p-4 shadow-2xl bg-white/95 backdrop-blur-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Digite o endereço e selecione da lista..."
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              className={`pl-10 pr-10 h-11 sm:h-12 bg-white text-base transition-all ${
                addressValidated 
                  ? "border-green-500 border-2 ring-2 ring-green-200" 
                  : "border-slate-200"
              }`}
              data-testid="address-search-input"
            />
            {/* Indicador de validação */}
            {addressValidated && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Check className="w-5 h-5 text-green-500" />
              </div>
            )}
            {searching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="spinner" />
              </div>
            )}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden bg-white max-h-60 overflow-y-auto">
              <div className="bg-blue-50 px-3 py-2 border-b border-blue-100">
                <p className="text-xs text-blue-600 font-medium">👆 Clique em um endereço abaixo para selecionar</p>
              </div>
              {searchResults.map((result, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectAddress(result)}
                  className="w-full text-left p-3 hover:bg-green-50 border-b border-slate-100 last:border-0 flex items-start gap-2 transition-colors"
                  data-testid={`search-result-${index}`}
                >
                  <MapPin className="w-4 h-4 text-[#FF4500] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-700 line-clamp-2">{result.display_name}</span>
                </button>
              ))}
            </div>
          )}

          {/* No results message */}
          {searchAddress.length >= 3 && !searching && searchResults.length === 0 && !addressValidated && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium">🔍 Nenhum endereço encontrado</p>
              <p className="text-xs text-yellow-700 mt-1">
                Dicas: Tente digitar apenas a rua e número, ou cole um link do Google Maps
              </p>
            </div>
          )}

          {/* Hint when typing but not selected */}
          {searchAddress.length > 0 && searchAddress.length < 3 && (
            <div className="mt-2 p-2 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Continue digitando para ver sugestões...</p>
            </div>
          )}

          {/* Selected Location Info */}
          {selectedLocation && addressValidated && !queryResult && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600 mb-3 line-clamp-2">{selectedLocation.address}</p>
              <p className="text-xs font-mono text-slate-500 mb-3">
                {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
              </p>
              <Button
                onClick={handleCheckViability}
                disabled={checking}
                className="w-full h-10 bg-[#FF4500] hover:bg-[#CC3700] text-white font-bold uppercase tracking-wide rounded-none"
                data-testid="check-viability-button"
              >
                {checking ? (
                  <div className="flex items-center gap-2">
                    <div className="spinner" />
                    <span>Verificando...</span>
                  </div>
                ) : (
                  <>
                    <Navigation className="w-4 h-4 mr-2" />
                    Verificar Viabilidade
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Query Result */}
          {queryResult && (
            <div className="mt-4 animate-slide-up">
              <div
                className={`p-4 rounded-lg ${
                  queryResult.is_viable
                    ? "bg-green-50 border-2 border-green-500"
                    : "bg-red-50 border-2 border-red-500"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className={`text-lg font-bold uppercase tracking-wide ${
                      queryResult.is_viable ? "text-green-600" : "text-red-600"
                    }`}
                    data-testid="viability-result"
                  >
                    {queryResult.is_viable ? "Viável" : "Não Viável"}
                  </span>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      queryResult.is_viable ? "bg-green-500" : "bg-red-500"
                    }`}
                  >
                    {queryResult.is_viable ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <X className="w-6 h-6 text-white" />
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">CTO mais próxima:</span>
                    <span className="font-mono font-semibold text-slate-900">
                      {queryResult.nearest_cto_name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Distância:</span>
                    <span
                      className={`font-mono font-bold ${
                        queryResult.is_viable ? "text-green-600" : "text-red-600"
                      }`}
                      data-testid="distance-result"
                    >
                      {queryResult.distance_meters?.toFixed(0)}m
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Limite:</span>
                    <span className="font-mono text-slate-500">{maxDistance}m</span>
                  </div>
                </div>

                {!queryResult.is_viable && (
                  <Button
                    onClick={() => setShowWaitlistModal(true)}
                    className="w-full mt-4 h-10 bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wide rounded-none"
                    data-testid="add-waitlist-button"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Adicionar à Fila
                  </Button>
                )}

                <Button
                  onClick={() => {
                    setQueryResult(null);
                    setSelectedLocation(null);
                    setSearchAddress("");
                    setAddressValidated(false);
                    setNearestCto(null);
                    lastSelectedAddressRef.current = null; // Reset selected address reference
                  }}
                  variant="outline"
                  className="w-full mt-2 h-9 text-slate-600 rounded-none"
                >
                  Nova Consulta
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Info - Hidden on mobile */}
      <div className="absolute bottom-4 right-4 z-20 hidden sm:block">
        <div className="glass-card rounded-lg px-4 py-2 bg-slate-900/90 text-white flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#FF4500] flex items-center justify-center text-sm font-bold">
            {user?.name?.charAt(0) || "V"}
          </div>
          <span className="text-sm">{user?.name}</span>
        </div>
      </div>

      {/* Footer - Última atualização do mapa */}
      <div className="absolute bottom-4 left-4 z-20">
        <div className="glass-card rounded-lg px-3 py-2 bg-slate-900/90 text-white text-xs">
          <span className="text-slate-400">Última atualização do mapa: </span>
          <span className="text-green-400 font-semibold">
            {lastKmzUpdate 
              ? new Date(lastKmzUpdate).toLocaleDateString("pt-BR")
              : "--/--/----"
            }
          </span>
        </div>
      </div>

      {/* Waitlist Modal */}
      <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar à Fila de Espera</DialogTitle>
            <DialogDescription>
              Cadastre o cliente para receber notificação quando houver cobertura na região.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="client_name">Nome do Cliente</Label>
              <Input
                id="client_name"
                value={waitlistForm.client_name}
                onChange={(e) =>
                  setWaitlistForm({ ...waitlistForm, client_name: e.target.value })
                }
                placeholder="Nome completo"
                data-testid="waitlist-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                value={waitlistForm.phone}
                onChange={(e) => setWaitlistForm({ ...waitlistForm, phone: e.target.value })}
                placeholder="(00) 00000-0000"
                data-testid="waitlist-phone-input"
              />
            </div>

            <div className="p-3 bg-slate-100 rounded-lg text-sm">
              <p className="font-semibold text-slate-700">Endereço:</p>
              <p className="text-slate-600 line-clamp-2">{selectedLocation?.address}</p>
              <p className="font-mono text-xs text-slate-500 mt-1">
                Distância da CTO: {queryResult?.distance_meters?.toFixed(0)}m
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowWaitlistModal(false)}
              className="rounded-none"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddToWaitlist}
              className="bg-[#FF4500] hover:bg-[#CC3700] rounded-none"
              data-testid="confirm-waitlist-button"
            >
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
